import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Copy, EnvelopeSimple, DeviceMobile, ShareNetwork, ChatText } from "@phosphor-icons/react";

const inputCls = "w-full h-10 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";

const INVITE_ROLE_OPTS = [
  ["store_manager", "Store Manager"], ["assistant_manager", "Assistant Manager"], ["shift_lead", "Shift Lead"],
  ["sales_associate", "Sales Associate"], ["stock_associate", "Stock Associate"],
  ["visual_merchandiser", "Visual Merchandiser"], ["read_only", "Read Only"],
];

export default function InviteManager() {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("sales_associate");
  const [storeId, setStoreId] = useState("");
  const [phone, setPhone] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [stores, setStores] = useState([]);
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  const load = useCallback(async () => {
    try { setInvites((await api.get("/invites")).data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => {
    load();
    api.get("/stores").then((r) => setStores(r.data)).catch(() => {});
  }, [load]);

  const linkFor = (t) => `${window.location.origin}/invite/${t}`;
  const copy = (t) => {
    const link = linkFor(t);
    const ok = () => toast.success("Invite link copied");
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = link; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy");
        document.body.removeChild(ta); ok();
      } catch { toast.error("Couldn't copy — long-press the link to copy it manually"); }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(ok).catch(fallback);
    } else { fallback(); }
  };
  const shareMsg = (t) => `You're invited to join Short Stack Hawaii LLC. Create your account here: ${linkFor(t)}`;
  const nativeShare = async (t) => {
    const link = linkFor(t);
    if (navigator.share) {
      try { await navigator.share({ title: "Join Short Stack Hawaii LLC", text: shareMsg(t), url: link }); return; }
      catch (e) { if (e.name === "AbortError") return; }
    }
    copy(t);
  };
  const smsHref = (t) => `sms:?&body=${encodeURIComponent(shareMsg(t))}`;
  const mailHref = (t, to) => `mailto:${to || ""}?subject=${encodeURIComponent("You're invited to join Short Stack Hawaii LLC")}&body=${encodeURIComponent(shareMsg(t))}`;
  const create = async () => {
    const hasEmail = !!email.trim();
    if (sendSms && !phone.trim()) { toast.error("Enter a mobile number to send the invite by text"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/invites", {
        email: hasEmail ? email.trim() : null, role, store_id: storeId || null,
        phone: phone.trim() || null, send_email: hasEmail && sendEmail, send_sms: sendSms,
      });
      setLastCreated({ token: data.token, email: data.email });
      setEmail(""); setStoreId(""); setPhone("");
      const via = [hasEmail && sendEmail && "email", sendSms && "text"].filter(Boolean);
      toast.success(via.length ? `Invite sent by ${via.join(" & ")}` : "Invite link created — share it below");
      copy(data.token);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const revoke = async (id) => { if (!window.confirm("Revoke this invite?")) return; try { await api.delete(`/invites/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const STATUS_BG = { pending: "#E0F2FE", accepted: "#DCFCE7", expired: "#F3F4F6", revoked: "#FEE2E2" };
  const roleLabel = (r) => (INVITE_ROLE_OPTS.find(([v]) => v === r) || [null, r])[1];

  return (
    <div className="space-y-4" data-testid="invite-tab">
      <div className="rounded-xl border-2 border-black bg-[#F9F9F6] p-4 space-y-3">
        <p className="font-bold flex items-center gap-2"><EnvelopeSimple size={16} weight="bold" /> Invite a new employee</p>
        <input data-testid="invite-email-input" className={inputCls} placeholder="employee@email.com (optional)" value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
        <p className="text-[11px] text-black/45 -mt-1">No email? Leave this blank — you'll get a link/QR to share, and they'll pick a <strong>username</strong> to log in with.</p>
        <div className="grid grid-cols-2 gap-2">
          <select data-testid="invite-role-select" className={inputCls} value={role} onChange={(e) => setRole(e.target.value)}>
            {INVITE_ROLE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid="invite-store-select" className={inputCls} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
            <option value="">No specific store</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div className="space-y-2 pt-1">
          <p className="text-xs font-bold uppercase tracking-wider text-black/40">Send invite via</p>
          <div className="flex gap-2">
            <button type="button" data-testid="invite-channel-email" onClick={() => setSendEmail((v) => !v)}
              className={`press flex-1 h-10 rounded-full border-2 border-black text-sm font-bold flex items-center justify-center gap-2 ${sendEmail ? "bg-primary text-white" : "bg-white text-black/60"}`}>
              <EnvelopeSimple size={14} weight="bold" /> Email
            </button>
            <button type="button" data-testid="invite-channel-sms" onClick={() => setSendSms((v) => !v)}
              className={`press flex-1 h-10 rounded-full border-2 border-black text-sm font-bold flex items-center justify-center gap-2 ${sendSms ? "bg-primary text-white" : "bg-white text-black/60"}`}>
              <DeviceMobile size={14} weight="bold" /> Text
            </button>
          </div>
          {sendSms && (
            <input data-testid="invite-phone-input" className={inputCls} placeholder="Mobile number e.g. (808) 555-1234"
              value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" />
          )}
        </div>

        <button data-testid="invite-create-btn" onClick={create} disabled={busy} className="press w-full h-11 rounded-full bg-primary text-white font-bold border-2 border-black flex items-center justify-center gap-2 disabled:opacity-60"><Plus size={16} weight="bold" /> Send invite</button>
        <p className="text-[11px] text-black/40">A single-use link is created and delivered by your selected method(s), and copied to your clipboard. The new hire sets their name & password to join. Links expire in 14 days.</p>
      </div>

      {lastCreated && (
        <div data-testid="invite-share-card" className="rounded-xl border-2 border-black bg-[#DCFCE7] p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="font-bold text-sm flex items-center gap-2"><ShareNetwork size={16} weight="bold" /> {lastCreated.email ? `Share invite with ${lastCreated.email}` : "Share this invite link"}</p>
            <button data-testid="invite-share-dismiss" onClick={() => setLastCreated(null)} className="text-xs font-bold text-black/40 hover:text-black">Dismiss</button>
          </div>
          <p className="text-[11px] text-black/50">Email delivery can land in spam on some providers (Yahoo, Hotmail). Share the link directly to be sure it arrives.</p>
          <div className="flex items-center gap-2 rounded-lg border-2 border-black bg-white px-3 h-10">
            <span className="text-xs truncate flex-1" data-testid="invite-share-link">{linkFor(lastCreated.token)}</span>
            <button data-testid="invite-share-copy" onClick={() => copy(lastCreated.token)} className="press shrink-0 text-xs font-bold flex items-center gap-1"><Copy size={12} weight="bold" /> Copy</button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button data-testid="invite-share-native" onClick={() => nativeShare(lastCreated.token)} className="press h-10 rounded-full border-2 border-black bg-primary text-white text-xs font-bold flex items-center justify-center gap-1"><ShareNetwork size={14} weight="bold" /> Share</button>
            <a data-testid="invite-share-text" href={smsHref(lastCreated.token)} className="press h-10 rounded-full border-2 border-black bg-white text-xs font-bold flex items-center justify-center gap-1"><ChatText size={14} weight="bold" /> Text</a>
            <a data-testid="invite-share-email" href={mailHref(lastCreated.token, lastCreated.email)} className="press h-10 rounded-full border-2 border-black bg-white text-xs font-bold flex items-center justify-center gap-1"><EnvelopeSimple size={14} weight="bold" /> Email</a>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs font-bold uppercase tracking-wider text-black/40">Invites</p>
        {invites.length === 0 && <p className="text-sm text-black/40">No invites yet.</p>}
        {invites.map((inv) => (
          <div key={inv.id} data-testid={`invite-item-${inv.id}`} className="rounded-xl border-2 border-black bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-bold text-sm truncate">{inv.has_email ? inv.email : "Link invite · no email"}</p>
                <p className="text-xs text-black/50">{roleLabel(inv.role)}{inv.phone ? ` · 📱 ${inv.phone}` : ""}</p>
              </div>
              <span className="text-[10px] font-bold px-2 py-1 rounded-full border-2 border-black shrink-0" style={{ backgroundColor: STATUS_BG[inv.status] || "#F3F4F6" }}>{inv.status}</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {inv.status === "pending" && <button data-testid={`invite-copy-${inv.id}`} onClick={() => copy(inv.token)} className="press h-8 px-3 rounded-full border-2 border-black bg-white text-xs font-bold flex items-center gap-1"><Copy size={12} weight="bold" /> Copy link</button>}
              {inv.status === "pending" && <button data-testid={`invite-share-${inv.id}`} onClick={() => nativeShare(inv.token)} className="press h-8 px-3 rounded-full border-2 border-black bg-white text-xs font-bold flex items-center gap-1"><ShareNetwork size={12} weight="bold" /> Share</button>}
              {inv.status === "pending" && <a data-testid={`invite-text-${inv.id}`} href={smsHref(inv.token)} className="press h-8 px-3 rounded-full border-2 border-black bg-white text-xs font-bold flex items-center gap-1"><ChatText size={12} weight="bold" /> Text</a>}
              <button data-testid={`invite-revoke-${inv.id}`} onClick={() => revoke(inv.id)} className="press h-8 px-3 rounded-full border-2 border-black bg-white text-xs font-bold flex items-center gap-1"><Trash size={12} weight="bold" /> {inv.status === "pending" ? "Revoke" : "Remove"}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
